// ============================================================
// DockerHub.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: The "Cloud Library" for Container Images.
// - Key Term: "Tagging" (Versioning your images).
// - Strategy: Visualize the Local-to-Cloud synchronization.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import VerifiedIcon from "@mui/icons-material/Verified";
import PublicIcon from "@mui/icons-material/Public";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HubIcon from "@mui/icons-material/Hub";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_BG = "#100720";   // Neural Purple
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(229, 201, 95, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const RegistryCard = styled(Paper)(({ theme, type }) => ({
    padding: theme.spacing(4),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    textAlign: "center",
    height: "100%",
    transition: "0.3s",
    "&:hover": {
        transform: "translateY(-10px)",
        borderColor: DOCKER_BLUE,
        backgroundColor: "rgba(229, 201, 95, 0.05)"
    }
}));

const TerminalCommand = styled(Box)(({ theme }) => ({
    backgroundColor: "#011627",
    padding: theme.spacing(2),
    borderRadius: "8px",
    fontFamily: "'Fira Code', monospace",
    color: "#ADDB67",
    fontSize: "0.85rem",
    border: "1px solid rgba(173, 219, 103, 0.2)",
    marginTop: theme.spacing(2)
}));

export default function DockerHub() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Distribution · Module 13"
                            sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Docker <span style={{ color: DOCKER_BLUE }}>Hub & Registries</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        A Registry is a storage and distribution system for named Docker images.
                        Docker Hub is the world's largest library for container images.
                    </Typography>
                </Box>

                {/* 2. PUSH & PULL WORKFLOW */}


                <Grid container spacing={4} sx={{ mb: 10 }} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <RegistryCard>
                            <CloudUploadIcon sx={{ fontSize: 50, color: DOCKER_BLUE, mb: 2 }} />
                            <Typography variant="h6" fontWeight={800}>Pushing to Cloud</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                Upload your locally built images to a registry so your
                                production servers or teammates can access them.
                            </Typography>
                            <TerminalCommand>$ docker push username/my-app:v1</TerminalCommand>
                        </RegistryCard>
                    </Grid>

                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                        <HubIcon sx={{ fontSize: 40, color: DOCKER_BLUE, opacity: 0.3 }} />
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <RegistryCard>
                            <CloudDownloadIcon sx={{ fontSize: 50, color: "#00ED64", mb: 2 }} />
                            <Typography variant="h6" fontWeight={800}>Pulling Images</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                Download pre-configured images (like MySQL, Nginx, or Python)
                                from the registry to your local machine.
                            </Typography>
                            <TerminalCommand>$ docker pull nginx:latest</TerminalCommand>
                        </RegistryCard>
                    </Grid>
                </Grid>

                {/* 3. IMAGE TYPES & SECURITY */}
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                    The <span style={{ color: DOCKER_BLUE }}>Trust</span> Hierarchy
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Official Images",
                            icon: <VerifiedIcon />,
                            desc: "Maintained by Docker and the original software authors. Highest security and performance standards."
                        },
                        {
                            title: "Verified Publishers",
                            icon: <SecurityIcon />,
                            desc: "Images provided by trusted companies (like MongoDB, Bitnami) that are verified by Docker Hub."
                        },
                        {
                            title: "Public Repositories",
                            icon: <PublicIcon />,
                            desc: "Community-built images. Useful, but should be scanned for vulnerabilities before production use."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4, textAlign: 'center' }}>
                                <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{item.icon}</Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom>{item.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{item.desc}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. PRIVATE REGISTRIES (Enterprise Focus) */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(229, 201, 95, 0.03)",
                    borderLeft: `5px solid ${DOCKER_BLUE}`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                        💡 Beyond Docker Hub
                    </Typography>
                    <Typography variant="body1" sx={{ color: BRAND.muted }}>
                        Many enterprises use private registries to host proprietary code.
                        Common alternatives include <strong>Amazon ECR</strong>, <strong>Google GCR</strong>,
                        and <strong>Azure ACR</strong>. The commands remain the same; only the URL changes.
                    </Typography>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Registry mastered. Let's move to CI/CD workflows.</Typography>
                    <Link to="/express-docker-cicd-workflow" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(229, 201, 95, 0.3)`,
                                "&:hover": { bgcolor: "#d4b950" }
                            }}
                        >
                            Next: Docker & CI/CD
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Validated Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
